import React, { useEffect, useState } from 'react'
import { Avatar, Input, Radio, RadioChangeEvent, Select } from 'antd'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { failureMessage, successMessage } from 'api/global'
import {
  useLeagueDirectCheckIn,
  useLeagueDirectCheckOut,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { useGetFamilyMembers } from 'api/family/family'
import { Person } from 'types/globalTypes'
import { getNameSignature } from 'helper/utility'
import ButtonSpinner from 'components/global/ButtonSpinner'
import Spinner from 'components/Spinner'
import LeagueParticipantAndDetails from './LeagueParticipantAndDetails'

interface Props {
  type: 'check-in' | 'check-out'
}

const LeagueCheckInOutTab: React.FC<Props> = ({ type }) => {
  const [pickUpPerson, setPickUpPerson] = useState('guardian')
  const [selectedAdultId, setSelectedAdultId] = useState<string | undefined>()
  const [notes, setNotes] = useState('')

  const {
    selectedAttendee,
    setCheckInModal,
    setCheckOutModal,
    setCheckInOutModalOpen,
    setSelectedAttendee,
  } = leagueAttendanceStore(store => ({
    selectedAttendee: store.selectedAttendee,
    setCheckInModal: store.setCheckInModal,
    setCheckOutModal: store.setCheckOutModal,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    setSelectedAttendee: store.setSelectedAttendee,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))

  const { mutate: directCheckIn, isLoading: checkInLoading } = useLeagueDirectCheckIn()
  const { mutate: directCheckOut, isLoading: checkOutLoading } = useLeagueDirectCheckOut()

  const {
    data: familyMembers,
    isLoading: familyMembersLoading,
    refetch: refetchFamilyMembers,
  } = useGetFamilyMembers(selectedAttendee?.familyId)

  useEffect(() => {
    if (type === 'check-out' && selectedAttendee?.familyId) {
      refetchFamilyMembers()
    }
  }, [type, selectedAttendee?.familyId])

  const isKid = selectedAttendee?.participantType === 'kid'
  const isLoading = checkInLoading || checkOutLoading

  const handleSubmit = () => {
    if (!selectedAttendee) return

    if (type === 'check-in') {
      if (!leagueData?._id) return failureMessage('Missing league data')

      const typeToKey: Record<string, string> = {
        kid: 'kidId',
        guardian: 'guardianId',
        authorizedAdult: 'authorizedAdultId',
        dropInAttendee: 'dropInAttendeeId',
      }
      const idKey = typeToKey[selectedAttendee.participantType ?? 'kid'] ?? 'kidId'

      const payload: any = {
        leagueId: leagueData._id,
        date: new Date().toISOString(),
        familyId: selectedAttendee.familyId,
        [idKey]: selectedAttendee._id,
        ...(notes ? { checkInNotes: notes } : {}),
      }

      directCheckIn(payload, {
        onSuccess: () => {
          successMessage('Checked in successfully')
          setNotes('')
          setSelectedAttendee(null)
          setCheckInModal(false)
          setCheckInOutModalOpen(true)
        },
        onError: () => failureMessage('Failed to check in'),
      })
    } else {
      if (!selectedAttendee.checkInId) {
        return failureMessage('No active check-in found for this attendee')
      }
      const payload = { checkInId: selectedAttendee.checkInId! }

      directCheckOut(payload, {
        onSuccess: () => {
          successMessage('Checked out successfully')
          setNotes('')
          setPickUpPerson('guardian')
          setSelectedAdultId(undefined)
          setSelectedAttendee(null)
          setCheckOutModal(false)
          setCheckInOutModalOpen(true)
        },
        onError: () => failureMessage('Failed to check out'),
      })
    }
  }

  return (
    <div className="flex gap-20px">
      <div className="flex-1 relative" style={{ minHeight: '35rem' }}>
        {type === 'check-out' && isKid && selectedAttendee?.familyId ? (
          <div className="pt-20px">
            <h3 className="text-14 w-600">Who is picking up?</h3>
            <Radio.Group
              onChange={(e: RadioChangeEvent) => setPickUpPerson(e.target.value)}
              value={pickUpPerson}
            >
              <div className="mt-20px radius-12 border-1px py-20px px-20px">
                <Radio value="guardian" className="radio">
                  Guardian
                </Radio>
              </div>
              <div className="mt-20px radius-12 border-1px py-20px px-20px">
                <Radio value="authorized-adult" className="radio">
                  Authorized Adults
                </Radio>
                {pickUpPerson === 'authorized-adult' && (
                  <>
                    <label className="input-label text-16 mt-20px">
                      Select Authorized Adult
                    </label>
                    <div>
                      <Select
                        className="h-50px w-full"
                        placeholder="Select authorized adult"
                        onChange={(v: string) => setSelectedAdultId(v)}
                      >
                        {familyMembersLoading ? (
                          <div className="flex justify-center py-10px">
                            <Spinner />
                          </div>
                        ) : familyMembers?.adults?.length ? (
                          familyMembers.adults.map((adult: Person) => (
                            <Select.Option key={adult._id} value={adult._id}>
                              <div className="flex align-center gap-16px mt-10px">
                                <Avatar size={30} src={adult.photo?.post}>
                                  {getNameSignature(
                                    `${adult.firstName} ${adult.lastName}`,
                                  )}
                                </Avatar>
                                <p>{`${adult.firstName} ${adult.lastName}`}</p>
                              </div>
                            </Select.Option>
                          ))
                        ) : (
                          <div className="py-10px">
                            <p>No adults found for this family</p>
                          </div>
                        )}
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </Radio.Group>
          </div>
        ) : null}

        <div style={{ maxWidth: '400px' }} className="pt-20px">
          <label className="input-label text-16">Notes</label>
          <Input
            className="custom-input-field w-full mt-10px"
            placeholder="Add a note"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <div className="flex gap-20px flex-col w-40p">
          <button
            className="btn-prim mt-20px"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <ButtonSpinner size={20} color="#000" />
            ) : type === 'check-out' ? (
              'Check-out'
            ) : (
              'Check-in'
            )}
          </button>
        </div>
      </div>

      {selectedAttendee?.familyId ? (
        <div className="bg-dark-gray px-30px py-20px">
          <LeagueParticipantAndDetails />
        </div>
      ) : null}
    </div>
  )
}

export default LeagueCheckInOutTab
